import { useState, useEffect } from 'react';
import { makeStyles } from 'design-system/product/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Button } from 'design-system';
import { Checkbox } from '@scale/scaleui';
/* eslint-disable restrict-imports/restrict-imports */
import { Tooltip } from 'react-scale/components/design-system';

import { CircularProgress, TextField } from 'react-scale/components/design-system/product';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import SAlert from 'react-s-alert';
import _ from 'lodash';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { QueryableTextAndResultsAnnotation } from '@scale/shared/types/response/textcollection';
import { QueryableTextAndResultsField } from '@scale/shared/types/params/textcollection';
import { useChatbotContext } from 'react-scale/dashboard.scale.com/contexts/ChatbotRankingContext';
import { BulbaBardEndpointOperation } from '@scale/shared/types/chat-completion';

interface QueryableTextAndResultsFieldSectionProps {
  field: QueryableTextAndResultsField;
  annotation: QueryableTextAndResultsAnnotation;
  setAnnotation: (annotation: QueryableTextAndResultsAnnotation) => void;
}

export default function QueryableTextAndResultsFieldSection({
  field,
  annotation,
  setAnnotation,
}: QueryableTextAndResultsFieldSectionProps) {
  const classes = useStyles();
  const searchResults: any[] = annotation?.response?.[0]?.searchResults || [];
  const searchTerm =
    annotation?.response?.[0]?.searchTerm || _.first(searchResults)?.query?.text || '';
  const citationReferenceIndex = annotation?.response?.[0]?.citationReferenceIndex || 0;
  const chatbotContext = useChatbotContext();
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | undefined>(undefined);
  const [checkedStatus, setCheckedStatus] = useState<any[]>(
    searchResults.map((result: any) => result.isChecked),
  );
  const handleCheckboxChange = (index: number, event: any) => {
    const newCheckedStatus = [...checkedStatus];
    newCheckedStatus[index] = event.target.checked;
    setCheckedStatus(newCheckedStatus);
    if (searchResults) {
      searchResults[index] = { ...searchResults[index], isChecked: event.target.checked };
      setAnnotation({
        ...annotation,
        response: [
          {
            searchTerm,
            searchResults,
            citationReferenceIndex,
          },
        ],
      });
    }
  };

  useEffect(() => {
    if (
      field.autopopulate_from_model_response &&
      chatbotContext?.additionalResponseData?.searchResults
    ) {
      const searchTerm =
        (_.first(chatbotContext.additionalResponseData.searchResults) as any)?.query || '';
      setCurrentSearch(searchTerm);
      setAnnotation({
        ...annotation,
        response: [
          {
            searchTerm,
            searchResults: chatbotContext.additionalResponseData.searchResults,
            citationReferenceIndex,
          },
        ],
      });
    }
  }, [chatbotContext?.additionalResponseData?.searchResults]);

  // Need this because we only want to update the search term in the annotation when the annotation changes
  const [currentSearch, setCurrentSearch] = useState('');
  useEffect(() => {
    setCurrentSearch(searchTerm);
  }, [searchTerm]);

  const [searchResultsLoading, setSearchResultsLoading] = useState(false);
  const [changedSearchQuery, setChangedSearchQuery] = useState(false);
  const isSearchEnabled = useUnleashFeatureFlag('bulba-is-google-search-enabled', {}, false);

  const handleSearchChange = (event: any) => {
    setCurrentSearch(event.target.value);
  };

  const onAutocomplete = async () => {
    setSubmitting(true);

    try {
      const history = chatbotContext.history;
      const prompt = _.last(history?.interactions)!.prompt;
      const topic = history.topic;
      await chatbotContext.submitPrompts([prompt], topic, {
        operation: BulbaBardEndpointOperation.BARD_SEARCH_OVERRIDE,
        search_results: searchResults,
        ignoreOldInteractionsInMessages: true,
      });
    } catch (_e) {
      setSubmissionError(
        'Error generating response. You may try again in 10 seconds. If you have already retried twice, please skip this task.',
      );
      setTimeout(() => {
        setSubmissionError(undefined);
        setSubmitting(false);
      }, 10000);
      return;
    }
    setChangedSearchQuery(false);
    setSubmitting(false);
  };

  const handleSearchSubmit = async () => {
    try {
      setSearchResultsLoading(true);
      // make a post call
      const { searchResults } = await apiFetch(null, `internal/contentUnderstanding/googleSearch`, {
        method: 'POST',
        body: {
          searchTerm: currentSearch,
        },
      });
      if (!searchResults) {
        SAlert.error('Error fetching search results, please try again or contact support');
      } else {
        setChangedSearchQuery(true);
        setAnnotation({
          ...annotation,
          response: [
            {
              searchTerm: currentSearch,
              searchResults,
              citationReferenceIndex,
            },
          ],
        });
      }
    } catch (e) {
      defaultApiFetchErrorHandler(e, { handleHttp: true });
    } finally {
      setSearchResultsLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.references}>{field.title || 'References'}</div>
      <div className={classes.searchContainer}>
        <TextField
          className={classes.searchInput}
          variant="outlined"
          size="small"
          label="Search query"
          value={currentSearch}
          onChange={handleSearchChange}
          disabled={!isSearchEnabled}
        />
        {isSearchEnabled && (
          <FontAwesomeIcon
            className={classes.searchIcon}
            icon="search"
            onClick={handleSearchSubmit}
          />
        )}
      </div>
      {searchResultsLoading ? (
        <CircularProgress size={30} />
      ) : (
        <div className={classes.results}>
          {searchResults.map((result, index) => (
            <div className={classes.resultContainer} key={index}>
              <div className={classes.resultContainerTitle} key={index}>
                <div>
                  <a
                    className={classes.link}
                    href={result.link || result.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className={classes.title}>
                      <b>{`[${citationReferenceIndex}.${index + 1}] ${
                        (result.title || result.sourceTitle) ?? ''
                      }`}</b>
                    </div>
                  </a>
                </div>
                <div>
                  <Checkbox
                    id="checkbox"
                    checked={checkedStatus[index] || false}
                    onChange={e => handleCheckboxChange(index, e)}
                  />
                </div>
              </div>
              <div className={classes.snippet}>{result.snippet}</div>
            </div>
          ))}
        </div>
      )}
      {submissionError && (
        <Alert severity="error" style={{ marginBottom: 10 }}>
          {submissionError}
        </Alert>
      )}
      {submitting && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '20px',
            color: '#6A5DF9',
            marginBottom: '10px',
          }}
        >
          <CircularProgress color="inherit" />
        </div>
      )}
      {field.use_autocomplete_button &&
        !!_.last(chatbotContext.history?.interactions)?.prompt &&
        searchResults && (
          <Tooltip
            title={
              changedSearchQuery
                ? 'Regenerate the model response based on the new search query and results.'
                : `Please change the search query and click the search icon before being able to use the autocomplete functionality. The
                autocomplete functionality will allow you to regenerate the model response based on an updated search query and results.`
            }
            placement="top"
            className={classes.tooltip}
          >
            <span>
              <Button
                style={{ width: '130px' }}
                disabled={!changedSearchQuery || submitting}
                type="primary"
                onClick={onAutocomplete}
              >
                Autocomplete
              </Button>
            </span>
          </Tooltip>
        )}
    </div>
  );
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'left',
    gap: theme.spacing(1),
  },
  references: {
    fontWeight: 'bold',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  searchInput: {
    marginRight: theme.spacing(2),
    flexGrow: 1,
  },
  searchIcon: {
    color: theme.palette.text.secondary,
    cursor: 'pointer',
  },
  tooltip: {
    fontSize: '14px',
    cursor: 'pointer',
  },
  results: {
    maxHeight: '300px',
    overflowY: 'auto',
  },
  resultContainer: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  resultContainerTitle: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  title: {
    fontWeight: 'bold',
    textDecoration: 'underline',
    // marginBottom: theme.spacing(1),
    color: '#1a0dab',
  },
  link: {
    color: '#1a0dab',
    textDecoration: 'none',
  },
  snippet: {
    color: '#545454',
  },
}));
